import { api, type ApiFailure } from './api';
// Refonte 2026-05-23 : origin (canal d'acquisition) déplacé depuis Opportunity
// vers Prospect. On réutilise le type `OpportunityOrigin` (l'enum SQL est le
// même — pas de renommage pour éviter une migration complexe).
import type { OpportunityOrigin } from './opportunities.api';

// Types miroir du backend (simplifiés - on ne reprend que ce que l'UI utilise).

export type ProspectType = 'company' | 'individual';
export type Civility = 'M' | 'Mme';

export interface ProspectListItem {
  id: string;
  type: ProspectType;
  // Champs entreprise (null pour les particuliers).
  siret: string | null;
  siren: string | null;
  companyName: string | null;
  // Champs particulier (null pour les entreprises).
  civility: Civility | null;
  firstName: string | null;
  lastName: string | null;
  birthDate: string | null;
  personalEmail: string | null;
  personalPhone: string | null;
  isPPE: boolean;
  // Communs.
  status: 'cold' | 'warm' | 'client';
  potential: 'low' | 'medium' | 'high' | 'very_high' | null;
  /** Canal d'acquisition (refonte 2026-05-23). Déplacé depuis Opportunity. */
  origin: OpportunityOrigin | null;
  addressCity: string | null;
  addressPostalCode: string | null;
  lastActivityAt: string | null;
  createdAt: string;
  assignedUser: { id: string; firstName: string; lastName: string; email: string } | null;
  assignedTeam: { id: string; name: string } | null;
  tags: Array<{ tag: Tag }>;
  _count: { opportunities: number; contacts: number };
}

export interface ProspectDetail extends ProspectListItem {
  legalForm: string | null;
  nafCode: string | null;
  nafLabel: string | null;
  addressStreet: string | null;
  employeeCount: number | null;
  revenue: string | null;
  website: string | null;
  creationDate: string | null;
  collectiveAgreement: string | null;
  idccCode: string | null;
  /** Téléphone de l'entreprise (standard). Company uniquement. */
  phone: string | null;
  observations: string | null;
  contacts: Contact[];
  establishments: Establishment[];
}

export type ContactRole = 'decision_maker' | 'prescriber' | 'manager' | 'accountant' | 'other';

export interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  role: ContactRole;
  phoneLandline: string | null;
  phoneMobile: string | null;
  email: string | null;
  isPrimary: boolean;
  /** Observation libre propre à ce rattachement. */
  observations: string | null;
  /** Nombre de prospects auxquels cette personne est rattachée (≥ 1). */
  linkedProspectsCount: number;
}

/** Résultat de la recherche d'attache (point 2) : la personne seule. */
export interface ContactSearchResult {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phoneMobile: string | null;
  phoneLandline: string | null;
}

export interface Establishment {
  id: string;
  prospectId: string;
  siret: string;
  name: string | null;
  addressStreet: string | null;
  addressCity: string | null;
  addressPostalCode: string | null;
  isHeadquarters: boolean;
}

export interface Tag {
  id: string;
  name: string;
  color: string | null;
}

export interface ListResponse<T> {
  data: T[];
  nextCursor: string | null;
}

export interface DuplicateHit {
  id: string;
  type: ProspectType;
  siret: string | null;
  companyName: string | null;
  displayName: string;
}

export interface DuplicatesResult {
  exact: (DuplicateHit & { assignedUserEmail: string | null }) | null;
  similar: Array<DuplicateHit & { score: number }>;
}

export interface InseeResult {
  siret: string;
  data: {
    siret: string;
    companyName: string;
    legalForm: string | null;
    nafCode: string | null;
    /** Libellé lisible de l'activité principale (fabrique.social.gouv.fr). */
    nafLabel: string | null;
    addressStreet: string | null;
    addressCity: string | null;
    addressPostalCode: string | null;
    employeeCount: number | null;
    creationDate: string | null;
    /** Libellé de la convention collective (fabrique.social.gouv.fr si dispo, fallback INSEE). */
    collectiveAgreement: string | null;
    /** Code IDCC à 4 chiffres (fabrique.social.gouv.fr). */
    idccCode: string | null;
    isHeadquarters: boolean;
    active: boolean;
  } | null;
}

export interface ProspectFilters {
  cursor?: string;
  limit?: number;

  // --- Type de prospect ---
  type?: ProspectType;

  // --- Qualification ---
  status?: ProspectListItem['status'];
  potential?: ProspectListItem['potential'];
  tag?: string;
  /** Refonte 2026-05-23 : filtre par origine du prospect (canal d'acquisition).
   *  CSV de codes enum + sentinelle `'none'` pour matcher les prospects sans
   *  origine. Exemple : `?origins=campaign,linkedin,none`. */
  origins?: string;

  // --- Affectation ---
  assigned_user_id?: string;
  team_id?: string;
  created_by_user_id?: string;

  // --- Recherche / géo ---
  search?: string;
  naf_code?: string;
  postal_code?: string;
  /** Département (2 ou 3 chiffres). Alias prefix-match sur `postal_code`. */
  department?: string;
  /** Rayon géographique : ville ou code postal + rayon (km). Les 2 sont
   *  indissociables. Le backend géocode via BAN (cache Redis 30j). */
  near_location?: string;
  radius_km?: number;

  // --- Activité commerciale ---
  has_opportunities?: boolean;
  has_signed_opp?: boolean;
  /** CSV de stages. */
  active_opp_stages?: string;
  /** CSV d'UUIDs. */
  subscribed_contract_type_ids?: string;
  min_signed_revenue?: number;
  in_sequence?: boolean;

  // --- Profil entreprise ---
  min_employee_count?: number;
  max_employee_count?: number;
  min_revenue?: number;
  max_revenue?: number;
  company_created_after?: string;
  company_created_before?: string;
  collective_agreement?: string;

  // --- Temporalité ---
  created_after?: string;
  created_before?: string;
  updated_after?: string;
  updated_before?: string;
  last_activity_after?: string;
  last_activity_before?: string;
  no_activity_since?: number;

  // --- Admin ---
  include_deleted?: boolean;
}

function qs(filters: ProspectFilters): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === null || v === '') continue;
    p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

function authHeaders(token: string | null) {
  return token ? { accessToken: token } : {};
}

export const prospectsApi = {
  list: (token: string, filters: ProspectFilters = {}) =>
    api.get<ListResponse<ProspectListItem>>(`/api/v1/prospects${qs(filters)}`, authHeaders(token)),

  get: (token: string, id: string) =>
    api.get<ProspectDetail>(`/api/v1/prospects/${id}`, authHeaders(token)),

  create: (token: string, body: Record<string, unknown>) =>
    api.post<ProspectDetail>('/api/v1/prospects', body, authHeaders(token)),

  update: (token: string, id: string, body: Record<string, unknown>) =>
    api.patch<ProspectDetail>(`/api/v1/prospects/${id}`, body, authHeaders(token)),

  delete: (token: string, id: string) =>
    api.delete<void>(`/api/v1/prospects/${id}`, authHeaders(token)),

  restore: (token: string, id: string) =>
    api.post<void>(`/api/v1/prospects/${id}/restore`, undefined, authHeaders(token)),

  checkDuplicates: (
    token: string,
    params: {
      siret?: string;
      company_name?: string;
      first_name?: string;
      last_name?: string;
      birth_date?: string;
    },
  ) =>
    api.get<DuplicatesResult>(
      `/api/v1/prospects/duplicates/check${qs(params as ProspectFilters)}`,
      authHeaders(token),
    ),

  insee: (token: string, siret: string) =>
    api.get<InseeResult>(`/api/v1/prospects/insee/${siret}`, authHeaders(token)),

  contacts: {
    list: (token: string, prospectId: string) =>
      api.get<Contact[]>(`/api/v1/prospects/${prospectId}/contacts`, authHeaders(token)),
    create: (token: string, prospectId: string, body: Partial<Contact>) =>
      api.post<Contact>(`/api/v1/prospects/${prospectId}/contacts`, body, authHeaders(token)),
    // Modèle n:m : édition et détachement sont imbriqués sous le prospect (le
    // role/isPrimary sont portés par le rattachement, pas par la personne).
    update: (token: string, prospectId: string, contactId: string, body: Partial<Contact>) =>
      api.patch<Contact>(
        `/api/v1/prospects/${prospectId}/contacts/${contactId}`,
        body,
        authHeaders(token),
      ),
    detach: (token: string, prospectId: string, contactId: string) =>
      api.delete<void>(`/api/v1/prospects/${prospectId}/contacts/${contactId}`, authHeaders(token)),
    // Rattacher un contact EXISTANT (point 2).
    attach: (
      token: string,
      prospectId: string,
      body: {
        contactId: string;
        role?: Contact['role'];
        isPrimary?: boolean;
        observations?: string | null;
      },
    ) =>
      api.post<Contact>(
        `/api/v1/prospects/${prospectId}/contacts/attach`,
        body,
        authHeaders(token),
      ),
    // Recherche cloisonnée pour la combobox d'attache.
    search: (token: string, q: string, excludeProspectId?: string) =>
      api.get<ContactSearchResult[]>(
        `/api/v1/contacts/search?q=${encodeURIComponent(q)}${
          excludeProspectId ? `&excludeProspectId=${excludeProspectId}` : ''
        }`,
        authHeaders(token),
      ),
  },

  establishments: {
    list: (token: string, prospectId: string) =>
      api.get<Establishment[]>(
        `/api/v1/prospects/${prospectId}/establishments`,
        authHeaders(token),
      ),
    create: (token: string, prospectId: string, body: Partial<Establishment>) =>
      api.post<Establishment>(
        `/api/v1/prospects/${prospectId}/establishments`,
        body,
        authHeaders(token),
      ),
  },

  tags: {
    list: (token: string) => api.get<Tag[]>('/api/v1/tags', authHeaders(token)),
    create: (token: string, body: { name: string; color?: string }) =>
      api.post<Tag>('/api/v1/tags', body, authHeaders(token)),
  },
};

export type { ApiFailure };
